/**
 * Live Scalp Localize helpers (S0–S3).
 * Pure functions for interpolated field, max focus, montage rules,
 * spike/event average, propagation frames, and band-power maps.
 */

import type { TopographyPoint } from "@features/Visualization/renderers/topographyContracts";
import type { EEGChannel } from "../services/EEGWaveformRenderer";
import { buildSelectionBandPower } from "./eegViewerSelectionAnalysis";
import {
  DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN,
  LIVE_TOPOGRAPHY_VALUE_UNIT,
  createEmptyLiveTopographyPreview,
  liveTopographySmoothingMs,
  resolveLiveTopographyAnchor,
  sampleLiveTopographyValue,
  type LiveTopographyAnchor,
  type LiveTopographyPreviewModel,
  type LiveTopographyTimeMode,
} from "./eegViewerLiveTopography";

/** S0 IFU freeze — assistive / adjunct only. */
export const LIVE_SCALP_IFU = {
  badge: "Adjunct",
  summary:
    "Live Scalp is decision-support topography. Confirm every material finding on raw EEG.",
  intendedUse:
    "Spatial awareness adjunct for electrode-field µV near the review cursor, and optional Localize field maps (interpolated surface, event peak/average, propagation, band power, age-matched normative z-score maps). Always verify morphology and timing on the raw waveform page.",
  excludedUse:
    "Excluded use: standalone diagnosis; treating demo or incomplete ESI as validated source localization; clinical conclusions from artifact-dominated, montage-mismatched, or normative-mismatched maps without raw EEG confirmation and normative provenance review.",
} as const;

export type LiveScalpViewMode = "live" | "localize";

export type LiveScalpLocalizeField =
  | "cursor"
  | "peak"
  | "average"
  | "band"
  | "propagation";

export type LiveScalpBandKey = "delta" | "theta" | "alpha" | "beta" | "gamma";

export type LiveScalpMaxFocus = {
  label: string;
  value: number;
  absoluteValue: number;
};

export type LiveScalpPropagationFrame = {
  timeSeconds: number;
  preview: LiveTopographyPreviewModel;
};

export type LiveScalpMontageKind =
  | "referential"
  | "bipolar"
  | "mixed"
  | "unknown";

export type BuildLiveTopographyFromChannelsArgs = {
  channels: EEGChannel[];
  sampleTimeSeconds: number;
  bufferStartSeconds: number;
  bufferEndSeconds: number | null;
  statusPrefix?: string;
  timeMode?: LiveTopographyTimeMode;
  valueUnit?: string;
  /** When set, use this absolute domain floor (e.g. shared propagation scale). */
  absDomainFloor?: number;
};

const BAND_KEYS: LiveScalpBandKey[] = [
  "delta",
  "theta",
  "alpha",
  "beta",
  "gamma",
];

export function findLiveScalpMaxFocus(
  points: TopographyPoint[],
): LiveScalpMaxFocus | null {
  let best: LiveScalpMaxFocus | null = null;
  for (const point of points) {
    if (!Number.isFinite(point.value) || !point.label) {
      continue;
    }
    const absoluteValue = Math.abs(point.value);
    if (!best || absoluteValue > best.absoluteValue) {
      best = {
        label: point.label,
        value: point.value,
        absoluteValue,
      };
    }
  }
  return best;
}

export function formatLiveScalpMaxFocus(
  focus: LiveScalpMaxFocus | null,
): string | null {
  if (!focus) {
    return null;
  }
  const signed =
    focus.value >= 0 ? `+${focus.value.toFixed(1)}` : focus.value.toFixed(1);
  return `Max @ ${focus.label} (${signed} ${LIVE_TOPOGRAPHY_VALUE_UNIT})`;
}

/**
 * Montage sampling rules (S1):
 * - Single token (Fp1, C3) → referential-style site
 * - Two tokens (F3-C3) → bipolar midpoint of both sites
 */
export function classifyLiveScalpMontageKind(
  channelNames: string[],
): LiveScalpMontageKind {
  let bipolar = 0;
  let referential = 0;
  for (const name of channelNames) {
    const anchor = resolveLiveTopographyAnchor(name);
    if (!anchor) {
      continue;
    }
    if (anchor.label.includes("-")) {
      bipolar += 1;
    } else {
      referential += 1;
    }
  }
  if (bipolar === 0 && referential === 0) {
    return "unknown";
  }
  if (bipolar > 0 && referential > 0) {
    return "mixed";
  }
  return bipolar > 0 ? "bipolar" : "referential";
}

export function formatLiveScalpMontageLabel(
  montageName: string | null | undefined,
  channelNames: string[],
): string {
  const kind = classifyLiveScalpMontageKind(channelNames);
  const name = (montageName || "").trim() || "Active montage";
  return `${name} · ${kind}`;
}

function aggregateChannelSamples(
  channels: EEGChannel[],
  sampleTimeSeconds: number,
  bufferStartSeconds: number,
): Map<string, { anchor: LiveTopographyAnchor; total: number; count: number }> {
  const aggregatedPoints = new Map<
    string,
    { anchor: LiveTopographyAnchor; total: number; count: number }
  >();

  for (const channel of channels) {
    const anchor = resolveLiveTopographyAnchor(channel.name);
    if (!anchor) {
      continue;
    }

    const sampledValue = sampleLiveTopographyValue(
      channel,
      sampleTimeSeconds,
      bufferStartSeconds,
    );
    if (sampledValue === null || !Number.isFinite(sampledValue)) {
      continue;
    }

    const existing = aggregatedPoints.get(anchor.key);
    if (existing) {
      existing.total += sampledValue;
      existing.count += 1;
    } else {
      aggregatedPoints.set(anchor.key, {
        anchor,
        total: sampledValue,
        count: 1,
      });
    }
  }

  return aggregatedPoints;
}

export function buildLiveTopographyPreviewFromChannels(
  args: BuildLiveTopographyFromChannelsArgs,
): LiveTopographyPreviewModel {
  const {
    channels,
    sampleTimeSeconds,
    bufferStartSeconds,
    bufferEndSeconds,
    statusPrefix = "Instant",
    timeMode = "instant",
    valueUnit = LIVE_TOPOGRAPHY_VALUE_UNIT,
    absDomainFloor = DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN,
  } = args;

  if (!channels.length) {
    return createEmptyLiveTopographyPreview(
      "Waiting for waveform buffer",
      "The live scalp preview appears once the current recording window is loaded.",
    );
  }

  const clampedTime =
    bufferEndSeconds !== null
      ? Math.min(
          Math.max(sampleTimeSeconds, bufferStartSeconds),
          bufferEndSeconds,
        )
      : Math.max(sampleTimeSeconds, bufferStartSeconds);

  const aggregated = aggregateChannelSamples(
    channels,
    clampedTime,
    bufferStartSeconds,
  );

  const points: TopographyPoint[] = Array.from(aggregated.values()).map(
    ({ anchor, total, count }) => ({
      x: anchor.x,
      y: anchor.y,
      z: anchor.z,
      value: total / count,
      label: anchor.label,
      geometryStatus: "resolved" as const,
      geometrySource: "live-eeg-viewer",
    }),
  );

  const bufferLabel =
    bufferEndSeconds !== null
      ? `Load ${bufferStartSeconds.toFixed(1)}-${bufferEndSeconds.toFixed(1)} s`
      : `Load from ${bufferStartSeconds.toFixed(1)} s`;

  if (points.length === 0) {
    return createEmptyLiveTopographyPreview(
      "No scalp-mapped channels",
      `${bufferLabel}. The current window does not expose channels that map cleanly onto scalp coordinates.`,
      channels.length,
    );
  }

  const maxAbsValue = Math.max(
    absDomainFloor,
    ...points.map((point) => Math.abs(point.value)),
  );
  const smoothingMs = liveTopographySmoothingMs();

  return {
    points,
    minValue: -maxAbsValue,
    maxValue: maxAbsValue,
    status: `${statusPrefix} @ ${clampedTime.toFixed(1)} s`,
    detail: `${points.length}/${channels.length} mapped · ±${smoothingMs} ms · ${bufferLabel}`,
    mappedChannels: points.length,
    totalChannels: channels.length,
    sampleTimeSeconds: clampedTime,
    timeMode,
    smoothingMs,
    valueUnit,
    bufferStartSeconds,
    bufferEndSeconds,
  };
}

/** Peak |µV| time within [start, end] across mapped channels. */
export function findLiveScalpPeakTimeSeconds(
  channels: EEGChannel[],
  bufferStartSeconds: number,
  windowStartSeconds: number,
  windowEndSeconds: number,
  stepSeconds = 0.01,
): number | null {
  if (!channels.length || windowEndSeconds <= windowStartSeconds) {
    return null;
  }

  let bestTime: number | null = null;
  let bestAbs = -1;

  for (
    let t = windowStartSeconds;
    t <= windowEndSeconds + 1e-9;
    t += stepSeconds
  ) {
    const aggregated = aggregateChannelSamples(channels, t, bufferStartSeconds);
    for (const { total, count } of aggregated.values()) {
      const abs = Math.abs(total / count);
      if (abs > bestAbs) {
        bestAbs = abs;
        bestTime = t;
      }
    }
  }

  return bestTime;
}

/** Average field across a time window (event / spike average). */
export function buildLiveTopographyAveragePreview(args: {
  channels: EEGChannel[];
  bufferStartSeconds: number;
  bufferEndSeconds: number | null;
  windowStartSeconds: number;
  windowEndSeconds: number;
  stepSeconds?: number;
  eventCount?: number;
}): LiveTopographyPreviewModel {
  const {
    channels,
    bufferStartSeconds,
    bufferEndSeconds,
    windowStartSeconds,
    windowEndSeconds,
    stepSeconds = 0.01,
    eventCount,
  } = args;

  if (!channels.length || windowEndSeconds <= windowStartSeconds) {
    return createEmptyLiveTopographyPreview(
      "Average unavailable",
      "Select a time window with loaded waveform data to build an average field map.",
      channels.length,
    );
  }

  const sums = new Map<
    string,
    { anchor: LiveTopographyAnchor; total: number; count: number }
  >();
  let sampleCount = 0;

  for (
    let t = windowStartSeconds;
    t <= windowEndSeconds + 1e-9;
    t += stepSeconds
  ) {
    const aggregated = aggregateChannelSamples(channels, t, bufferStartSeconds);
    sampleCount += 1;
    for (const [, entry] of aggregated) {
      const mean = entry.total / entry.count;
      const existing = sums.get(entry.anchor.key);
      if (existing) {
        existing.total += mean;
        existing.count += 1;
      } else {
        sums.set(entry.anchor.key, {
          anchor: entry.anchor,
          total: mean,
          count: 1,
        });
      }
    }
  }

  const points: TopographyPoint[] = Array.from(sums.values()).map(
    ({ anchor, total, count }) => ({
      x: anchor.x,
      y: anchor.y,
      z: anchor.z,
      value: total / count,
      label: anchor.label,
      geometryStatus: "resolved" as const,
      geometrySource: "live-eeg-viewer-average",
    }),
  );

  if (points.length === 0) {
    return createEmptyLiveTopographyPreview(
      "Average unavailable",
      "No scalp-mapped channels in the selected window.",
      channels.length,
    );
  }

  const maxAbsValue = Math.max(
    DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN,
    ...points.map((point) => Math.abs(point.value)),
  );
  const provenance =
    eventCount != null && eventCount > 0
      ? `N=${eventCount} events`
      : `${sampleCount} samples`;

  return {
    points,
    minValue: -maxAbsValue,
    maxValue: maxAbsValue,
    status: `Average ${windowStartSeconds.toFixed(2)}–${windowEndSeconds.toFixed(2)} s`,
    detail: `${points.length}/${channels.length} mapped · ${provenance}`,
    mappedChannels: points.length,
    totalChannels: channels.length,
    sampleTimeSeconds: (windowStartSeconds + windowEndSeconds) / 2,
    timeMode: "instant",
    smoothingMs: liveTopographySmoothingMs(),
    valueUnit: LIVE_TOPOGRAPHY_VALUE_UNIT,
    bufferStartSeconds,
    bufferEndSeconds,
  };
}

/** Average field at discrete event times (confirmed spikes / annotations). */
export function buildLiveTopographyEventAveragePreview(args: {
  channels: EEGChannel[];
  bufferStartSeconds: number;
  bufferEndSeconds: number | null;
  eventTimesSeconds: number[];
  halfWindowSeconds?: number;
}): LiveTopographyPreviewModel {
  const {
    channels,
    bufferStartSeconds,
    bufferEndSeconds,
    eventTimesSeconds,
    halfWindowSeconds = 0.04,
  } = args;

  const times = eventTimesSeconds.filter((t) => Number.isFinite(t));
  if (times.length === 0) {
    return createEmptyLiveTopographyPreview(
      "No events",
      "Confirm spike/event times (AI Triage or annotations) to build an average field map.",
      channels.length,
    );
  }

  const sums = new Map<
    string,
    { anchor: LiveTopographyAnchor; total: number; count: number }
  >();

  for (const eventTime of times) {
    const preview = buildLiveTopographyAveragePreview({
      channels,
      bufferStartSeconds,
      bufferEndSeconds,
      windowStartSeconds: eventTime - halfWindowSeconds,
      windowEndSeconds: eventTime + halfWindowSeconds,
      stepSeconds: 0.01,
    });
    for (const point of preview.points) {
      const anchor = resolveLiveTopographyAnchor(point.label || "");
      if (!anchor) {
        continue;
      }
      const existing = sums.get(anchor.key);
      if (existing) {
        existing.total += point.value;
        existing.count += 1;
      } else {
        sums.set(anchor.key, {
          anchor,
          total: point.value,
          count: 1,
        });
      }
    }
  }

  const points: TopographyPoint[] = Array.from(sums.values()).map(
    ({ anchor, total, count }) => ({
      x: anchor.x,
      y: anchor.y,
      z: anchor.z,
      value: total / count,
      label: anchor.label,
      geometryStatus: "resolved" as const,
      geometrySource: "live-eeg-viewer-event-average",
    }),
  );

  if (points.length === 0) {
    return createEmptyLiveTopographyPreview(
      "Average unavailable",
      "Events did not map onto scalp coordinates in the loaded buffer.",
      channels.length,
    );
  }

  const maxAbsValue = Math.max(
    DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN,
    ...points.map((point) => Math.abs(point.value)),
  );

  return {
    points,
    minValue: -maxAbsValue,
    maxValue: maxAbsValue,
    status: `Event average · N=${times.length}`,
    detail: `${points.length}/${channels.length} mapped · ±${Math.round(halfWindowSeconds * 1000)} ms per event`,
    mappedChannels: points.length,
    totalChannels: channels.length,
    sampleTimeSeconds: times[0] ?? null,
    timeMode: "instant",
    smoothingMs: Math.round(halfWindowSeconds * 1000),
    valueUnit: LIVE_TOPOGRAPHY_VALUE_UNIT,
    bufferStartSeconds,
    bufferEndSeconds,
  };
}

function sliceChannelWindow(
  channel: EEGChannel,
  windowStartSeconds: number,
  windowEndSeconds: number,
  bufferStartSeconds: number,
): number[] | null {
  if (!Number.isFinite(channel.sampleRate) || channel.sampleRate <= 0) {
    return null;
  }
  const startIndex = Math.max(
    0,
    Math.floor((windowStartSeconds - bufferStartSeconds) * channel.sampleRate),
  );
  const endIndex = Math.min(
    channel.data.length,
    Math.ceil((windowEndSeconds - bufferStartSeconds) * channel.sampleRate),
  );
  if (endIndex - startIndex < 8) {
    return null;
  }
  return Array.from(channel.data.slice(startIndex, endIndex));
}

/** Relative band-power topographic map (0–100%). */
export function buildLiveTopographyBandPowerPreview(args: {
  channels: EEGChannel[];
  bufferStartSeconds: number;
  bufferEndSeconds: number | null;
  windowStartSeconds: number;
  windowEndSeconds: number;
  band: LiveScalpBandKey;
}): LiveTopographyPreviewModel {
  const {
    channels,
    bufferStartSeconds,
    bufferEndSeconds,
    windowStartSeconds,
    windowEndSeconds,
    band,
  } = args;

  const sums = new Map<
    string,
    { anchor: LiveTopographyAnchor; total: number; count: number }
  >();

  for (const channel of channels) {
    const anchor = resolveLiveTopographyAnchor(channel.name);
    if (!anchor) {
      continue;
    }
    const samples = sliceChannelWindow(
      channel,
      windowStartSeconds,
      windowEndSeconds,
      bufferStartSeconds,
    );
    if (!samples) {
      continue;
    }
    const powers = buildSelectionBandPower(samples, channel.sampleRate);
    const fraction = powers[band];
    if (fraction == null || !Number.isFinite(fraction)) {
      continue;
    }
    const percent = fraction * 100;
    const existing = sums.get(anchor.key);
    if (existing) {
      existing.total += percent;
      existing.count += 1;
    } else {
      sums.set(anchor.key, { anchor, total: percent, count: 1 });
    }
  }

  const points: TopographyPoint[] = Array.from(sums.values()).map(
    ({ anchor, total, count }) => ({
      x: anchor.x,
      y: anchor.y,
      z: anchor.z,
      value: total / count,
      label: anchor.label,
      geometryStatus: "resolved" as const,
      geometrySource: "live-eeg-viewer-band",
    }),
  );

  if (points.length === 0) {
    return createEmptyLiveTopographyPreview(
      "Band map unavailable",
      "Need a longer loaded window to estimate relative band power by electrode.",
      channels.length,
    );
  }

  const maxValue = Math.max(10, ...points.map((point) => point.value));

  return {
    points,
    minValue: 0,
    maxValue,
    status: `${band} power ${windowStartSeconds.toFixed(1)}–${windowEndSeconds.toFixed(1)} s`,
    detail: `${points.length}/${channels.length} mapped · relative %`,
    mappedChannels: points.length,
    totalChannels: channels.length,
    sampleTimeSeconds: (windowStartSeconds + windowEndSeconds) / 2,
    timeMode: "instant",
    smoothingMs: 0,
    valueUnit: "%",
    bufferStartSeconds,
    bufferEndSeconds,
  };
}

export function buildLiveScalpPropagationFrames(args: {
  channels: EEGChannel[];
  bufferStartSeconds: number;
  bufferEndSeconds: number | null;
  windowStartSeconds: number;
  windowEndSeconds: number;
  frameCount?: number;
}): LiveScalpPropagationFrame[] {
  const {
    channels,
    bufferStartSeconds,
    bufferEndSeconds,
    windowStartSeconds,
    windowEndSeconds,
    frameCount = 8,
  } = args;

  if (windowEndSeconds <= windowStartSeconds || frameCount < 2) {
    return [];
  }

  const previews: LiveTopographyPreviewModel[] = [];
  for (let i = 0; i < frameCount; i += 1) {
    const t =
      windowStartSeconds +
      ((windowEndSeconds - windowStartSeconds) * i) / (frameCount - 1);
    previews.push(
      buildLiveTopographyPreviewFromChannels({
        channels,
        sampleTimeSeconds: t,
        bufferStartSeconds,
        bufferEndSeconds,
        statusPrefix: "Frame",
        absDomainFloor: 0,
      }),
    );
  }

  const sharedAbs = Math.max(
    DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN,
    ...previews.flatMap((preview) =>
      preview.points.map((point) => Math.abs(point.value)),
    ),
  );

  return previews.map((preview) => ({
    timeSeconds: preview.sampleTimeSeconds ?? windowStartSeconds,
    preview: {
      ...preview,
      minValue: -sharedAbs,
      maxValue: sharedAbs,
      status: `Prop @ ${(preview.sampleTimeSeconds ?? 0).toFixed(2)} s`,
    },
  }));
}

/** Convert Live Scalp ±1 coords to 0–1 for IDW grid renderers. */
export function liveScalpPointsToNormalizedElectrodes(
  points: TopographyPoint[],
): Array<{ name: string; x: number; y: number; value: number }> {
  return points.map((point) => ({
    name: point.label || "?",
    x: (point.x + 1) / 2,
    y: (1 - point.y) / 2,
    value: point.value,
  }));
}

/** Simple IDW interpolate at normalized (0–1) coordinates. */
export function idwInterpolateNormalized(
  electrodes: Array<{ x: number; y: number; value: number }>,
  x: number,
  y: number,
  power = 2,
): number {
  let numerator = 0;
  let denominator = 0;
  for (const electrode of electrodes) {
    const dx = x - electrode.x;
    const dy = y - electrode.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < 1e-6) {
      return electrode.value;
    }
    const weight = 1 / distance ** power;
    numerator += weight * electrode.value;
    denominator += weight;
  }
  return denominator > 0 ? numerator / denominator : 0;
}

export function isLiveScalpBandKey(value: string): value is LiveScalpBandKey {
  return (BAND_KEYS as string[]).includes(value);
}

/**
 * Map a topography electrode/site label (e.g. F3 or F3-C3) onto montage
 * channel labels so map→trace lock highlights real waveform rows.
 */
export function resolveLiveScalpElectrodeToChannelLabels(
  topographyLabel: string,
  montageChannelLabels: string[],
): string[] {
  const wanted = topographyLabel.trim().toUpperCase();
  if (!wanted || montageChannelLabels.length === 0) {
    return [];
  }

  const exact: string[] = [];
  const siteHits: string[] = [];
  const wantedSites = wanted.split(/[-–—/]+/).filter(Boolean);

  for (const channelLabel of montageChannelLabels) {
    const anchor = resolveLiveTopographyAnchor(channelLabel);
    if (!anchor) {
      continue;
    }
    const anchorLabel = anchor.label.toUpperCase();
    if (anchorLabel === wanted) {
      exact.push(channelLabel);
      continue;
    }
    const anchorSites = anchorLabel.split("-").filter(Boolean);
    if (
      wantedSites.length === 1 &&
      anchorSites.includes(wantedSites[0])
    ) {
      siteHits.push(channelLabel);
      continue;
    }
    if (
      wantedSites.length > 1 &&
      wantedSites.every((site) => anchorSites.includes(site))
    ) {
      siteHits.push(channelLabel);
    }
  }

  return exact.length > 0 ? exact : siteHits;
}

export { BAND_KEYS as LIVE_SCALP_BAND_KEYS };
